import * as React from 'react';
import { observer } from 'mobx-react-lite';
import {
    CalendarExpandableAllDayWell,
    combineHandlers,
    CalendarDayContent,
} from 'owa-calendar-surface-core';
import {
    useAllDayWellHooks,
    useAllDayWellResizeHooks,
    CalendarSurfaceTransformProps,
    DroppableType,
    CalendarSurfaceStore,
} from 'owa-calendar-surface-dategrid';
import { useSurfaceDroppableHooks } from 'owa-calendar-surface-dategrid-common-dnd';

export interface DayGridAllDayAdditionalProps {
    calendarFolderId?: string;
    viewState: CalendarSurfaceStore;
    calendarConfig: CalendarSurfaceTransformProps;
    calendarDayContent: CalendarDayContent;
}

export default observer(function DayGridAllDayComponent({
    className,
    children,
    handlers,
    additionalProps,
}: {
    className?: string;
    children?: React.ReactNode;
    handlers?: React.DOMAttributes<HTMLElement>;
    additionalProps: DayGridAllDayAdditionalProps;
}) {
    const { viewState, calendarFolderId, calendarDayContent, calendarConfig } = additionalProps;
    const {
        visibleRowCount,
        allDayUncollapsedRowCount,
    } = calendarDayContent.allDayContentProperties();
    const ref = React.useRef<HTMLDivElement>();

    const surfaceDroppableBehaviorHook = useSurfaceDroppableHooks({
        store: viewState,
        columnProvider: calendarConfig.columnProvider,
        dragDropConfig: calendarConfig.dragDropConfig,
        droppableType: DroppableType.AllDay,
        calendarFolderId: calendarFolderId,
    });

    const allDayWellHooks = useAllDayWellHooks({
        store: viewState,
        config: calendarConfig,
        calendarFolderId: calendarFolderId,
    });

    const allDayWellResizeHooks = useAllDayWellResizeHooks({
        store: viewState,
    })(ref);

    const handlersToAttach = combineHandlers(
        surfaceDroppableBehaviorHook.handlers,
        allDayWellResizeHooks.handlers,
        allDayWellHooks.handlers,
        handlers
    );

    // for the expandable all day well, the children passed from the parent get injected into the inner content div, but the
    // hook content gets injected in the wrapper div above it, so we pass the children in two different properties
    return (
        <CalendarExpandableAllDayWell
            allDayRef={ref}
            allDayContent={children}
            visibleRowCount={visibleRowCount}
            allDayUncollapsedRowCount={allDayUncollapsedRowCount}
            children={allDayUncollapsedRowCount ? allDayWellResizeHooks.children : null}
            isSplitView={viewState.isInSplitView}
            handlers={handlersToAttach}
        />
    );
});
